import { memo, type ReactNode } from "react";
import { useRef, useCallback } from "react";
import {
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonItem,
  IonList,
} from "@ionic/react";
import type { ITask } from "../types";
import TaskCard from "./TaskCard";

export interface SwipeAction {
  label: string;
  icon?: ReactNode;
  bg: string;
  fg?: string;
  onAction: (taskUuid: string) => void;
  shouldShow?: (task: ITask) => boolean;
}

type SwipeStartProp = SwipeAction | ((task: ITask) => SwipeAction | null);

interface TaskListProps {
  tasks: ITask[];
  showMoveToToday?: boolean;
  showMoveToBacklog?: boolean;
  showComplete?: boolean;
  showStatus?: boolean;
  swipeStart?: SwipeStartProp;
  swipeEnd?: SwipeAction;
  /** Optional secondary action on the end side (rendered before the primary, no auto-trigger). */
  swipeEndSecondary?: SwipeAction | ((task: ITask) => SwipeAction | null);
}

/** Drag ratio past which a full-swipe auto-triggers the action. */
const FULL_SWIPE_THRESHOLD = 1.8;

/** Shared IonItem style — extracted to avoid re-creating on every render. */
const ITEM_STYLE = {
  "--background": "hsl(var(--surface))",
  "--padding-start": "0",
  "--padding-end": "0",
  "--inner-padding-start": "0",
  "--inner-padding-end": "0",
  "--min-height": "0",
  "--border-width": "0",
  "--inner-border-width": "0",
  "--border-color": "transparent",
} as React.CSSProperties;

const EMPTY_OPTION_STYLE = {
  "--background": "transparent",
  width: "1px",
} as React.CSSProperties;

function resolveStart(
  swipeStart: SwipeStartProp | undefined,
  task: ITask
): SwipeAction | null {
  if (!swipeStart) return null;
  if (typeof swipeStart === "function") return swipeStart(task);
  if (!swipeStart.shouldShow || swipeStart.shouldShow(task)) return swipeStart;
  return null;
}

export const SwipeableItem = memo(function SwipeableItem({
  task,
  showMoveToToday,
  showMoveToBacklog,
  showComplete,
  showStatus,
  swipeStart,
  swipeEnd,
  swipeEndSecondary,
}: {
  task: ITask;
  showMoveToToday?: boolean;
  showMoveToBacklog?: boolean;
  showComplete?: boolean;
  showStatus?: boolean;
  swipeStart?: SwipeStartProp;
  swipeEnd?: SwipeAction;
  swipeEndSecondary?: SwipeAction | ((task: ITask) => SwipeAction | null);
}) {
  const slidingRef = useRef<HTMLIonItemSlidingElement>(null);
  const firedRef = useRef(false);

  // Use refs for action callbacks to avoid re-creating the drag handler
  const startRef = useRef(resolveStart(swipeStart, task));
  startRef.current = resolveStart(swipeStart, task);
  const endRef = useRef(swipeEnd);
  endRef.current = swipeEnd;

  const resolvedStart = startRef.current;
  const resolvedEndSecondary =
    typeof swipeEndSecondary === "function"
      ? swipeEndSecondary(task)
      : swipeEndSecondary && (!swipeEndSecondary.shouldShow || swipeEndSecondary.shouldShow(task))
        ? swipeEndSecondary
        : null;

  const handleSwipeStart = useCallback(async () => {
    startRef.current?.onAction(task.uuid);
    await slidingRef.current?.close();
  }, [task.uuid]);

  const handleSwipeEnd = useCallback(async () => {
    endRef.current?.onAction(task.uuid);
    await slidingRef.current?.close();
  }, [task.uuid]);

  const handleSwipeEndSecondary = useCallback(async () => {
    resolvedEndSecondary?.onAction(task.uuid);
    await slidingRef.current?.close();
  }, [task.uuid, resolvedEndSecondary]);

  // Stable drag handler — reads from refs, never changes identity
  const handleDrag = useCallback(
    (e: CustomEvent) => {
      const ratio: number = e.detail.ratio;

      if (firedRef.current) {
        if (Math.abs(ratio) < 0.1) firedRef.current = false;
        return;
      }

      // Ionic ionDrag ratio: negative = start side opening (swipe right),
      // positive = end side opening (swipe left)
      if (ratio < -FULL_SWIPE_THRESHOLD && startRef.current) {
        firedRef.current = true;
        startRef.current.onAction(task.uuid);
        slidingRef.current?.close();
      } else if (ratio > FULL_SWIPE_THRESHOLD && endRef.current) {
        firedRef.current = true;
        endRef.current.onAction(task.uuid);
        slidingRef.current?.close();
      }
    },
    [task.uuid]
  );

  const handleClose = useCallback(() => {
    slidingRef.current?.close();
  }, []);

  const hasSwipe = swipeStart || swipeEnd || resolvedEndSecondary;

  const cardContent = (
    <div className="w-full">
      <TaskCard
        task={task}
        showMoveToToday={showMoveToToday}
        showMoveToBacklog={showMoveToBacklog}
        showComplete={showComplete}
        showStatus={showStatus}
      />
    </div>
  );

  if (!hasSwipe) return cardContent;

  return (
    <div className="swipeable-task-item">
      <IonItemSliding ref={slidingRef} onIonDrag={handleDrag}>
        <IonItemOptions side="start">
          {resolvedStart ? (
            <IonItemOption
              expandable
              onClick={handleSwipeStart}
              style={{
                "--background": resolvedStart.bg,
                "--color": resolvedStart.fg || "#fff",
              } as React.CSSProperties}
            >
              <div className="flex flex-col items-center gap-0.5 px-3">
                {resolvedStart.icon}
                <span className="text-xs font-medium">{resolvedStart.label}</span>
              </div>
            </IonItemOption>
          ) : (
            <IonItemOption onClick={handleClose} style={EMPTY_OPTION_STYLE} />
          )}
        </IonItemOptions>

        <IonItem lines="none" detail={false} style={ITEM_STYLE}>
          {cardContent}
        </IonItem>

        <IonItemOptions side="end">
          {resolvedEndSecondary && (
            <IonItemOption
              onClick={handleSwipeEndSecondary}
              style={{
                "--background": resolvedEndSecondary.bg,
                "--color": resolvedEndSecondary.fg || "#fff",
              } as React.CSSProperties}
            >
              <div className="flex flex-col items-center gap-0.5 px-3">
                {resolvedEndSecondary.icon}
                <span className="text-xs font-medium">{resolvedEndSecondary.label}</span>
              </div>
            </IonItemOption>
          )}
          {swipeEnd ? (
            <IonItemOption
              expandable
              onClick={handleSwipeEnd}
              style={{
                "--background": swipeEnd.bg,
                "--color": swipeEnd.fg || "#fff",
              } as React.CSSProperties}
            >
              <div className="flex flex-col items-center gap-0.5 px-3">
                {swipeEnd.icon}
                <span className="text-xs font-medium">{swipeEnd.label}</span>
              </div>
            </IonItemOption>
          ) : !resolvedEndSecondary ? (
            <IonItemOption onClick={handleClose} style={EMPTY_OPTION_STYLE} />
          ) : null}
        </IonItemOptions>
      </IonItemSliding>
    </div>
  );
});

export default function TaskList({
  tasks,
  showMoveToToday,
  showMoveToBacklog,
  showComplete,
  showStatus,
  swipeStart,
  swipeEnd,
  swipeEndSecondary,
}: TaskListProps) {
  return (
    <IonList
      lines="none"
      style={{ background: "transparent", padding: 0 }}
      className="space-y-2"
    >
      {tasks.map((task) => (
        <SwipeableItem
          key={task.uuid}
          task={task}
          showMoveToToday={showMoveToToday}
          showMoveToBacklog={showMoveToBacklog}
          showComplete={showComplete}
          showStatus={showStatus}
          swipeStart={swipeStart}
          swipeEnd={swipeEnd}
          swipeEndSecondary={swipeEndSecondary}
        />
      ))}
    </IonList>
  );
}
